import {
  isLauncherJourney,
  type Journey as JourneyV3
} from '@epilot/journey-logic-commons'
import { up } from '@epilot/journey-logic-commons/src/schemas/v4'
import type { Journey } from '@epilot/journey-logic-commons/src/types/next'

import type { JourneyChangedAction } from '../types/actions'
import { APP_ERROR } from '../types/enums'

import { actionFactory } from './factory'
import { setAppError } from './setAppError'

/**
 * Responsible for processing a Journey config after fetching from the API or receiving it via
 * event in case of a preview session.
 */
export const journeyChanged = actionFactory<JourneyChangedAction>(
  (state, { journey }) => {
    let castedJourney = journey as JourneyV3
    const id = journey.journeyId

    if (!id) {
      return void setAppError(
        {
          error: APP_ERROR.JOURNEY_NOT_FOUND,
          reason: 'Journey Id is missing.'
        },
        state
      )
    }

    /**
     * Temporarily enforce Concorde for all Journeys regardless of their original setting.
     *
     * We've reached the point where all Journeys should be running with the new design, but there might
     * still be Journeys out there (i.e. orgs not listed as active customers like test orgs) without
     * this setting ON.
     *
     * TODO: remove after the cleanup of non-Concorde code
     *
     * {@link https://epilot.slack.com/archives/C0708KUGJVC/p1749563687491659}
     */

    if (!castedJourney.settings?.useNewDesign) {
      castedJourney = structuredClone(castedJourney) // can be a read-only object
      if (castedJourney.settings) {
        castedJourney.settings.useNewDesign = true
      } else {
        castedJourney.settings = {
          useNewDesign: true
        }
      }
    }

    state.journeys[id] = {
      originalJourney: castedJourney,
      journey: trySchemaUp(castedJourney)
    }

    // caches the launcher-type check for further use
    if (state.journeys[id].journey === undefined) {
      state.journeys[id].isLauncherType = isLauncherJourney(castedJourney.steps)
    }
  },
  'journeyChanged'
)

/**
 * Safely attempts to transform the Journey schema from v3 to v4.
 * If the transformation fails, it returns undefined and logs the error.
 */
export const trySchemaUp = (journey: JourneyV3): Journey | undefined => {
  try {
    return up(journey)
  } catch (originalError) {
    const error = new Error(`Error transforming Journey schema`)

    error.cause = originalError

    // eslint-disable-next-line no-console
    console.error(error, { journeyId: journey.journeyId })

    return undefined
  }
}
